import React from 'react';
import BaseInput from '../Base/BaseInput';
import BaseSelect from '../Base/BaseSelect';
import MultiSelectSearchable from '../Base/MultiSelectSearchable';
import { Driver } from '../../models/DriverModel';
import {
  finalizeFormTextField,
  sanitizeFormTextField,
} from '../../utils/textInputSanitize';
import './styles/DriverForm.css';

type Props = {
  value: Driver;
  onChange: (c: Driver) => void;
  error?: string | null;
  passes: { value: string; label: string }[];
  shifts: { value: string; label: string }[];
  shiftGroups: { value: string; label: string }[];
  vehicles: { value: string; label: string }[];
  fieldErrors?: Record<string, string>;
  showStatusField?: boolean;
};

const DriverForm: React.FC<Props> = ({
  value,
  onChange,
  error,
  fieldErrors = {},
  passes,
  shifts,
  shiftGroups,
  vehicles,
  showStatusField = true,
}) => {
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    onChange({ ...value, [name]: sanitizeFormTextField(name, v) });
  };

  const handleTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    const next = finalizeFormTextField(name, v);
    if (next !== (value as any)[name]) {
      onChange({ ...value, [name]: next });
    }
  };

  const handlePassesChange = (selected: number[]) => {
    onChange({ ...value, passes: selected });
  };

  const handleShiftGroupsChange = (selected: number[]) => {
    onChange({ ...value, shift_groups: selected });
  };

  const handleSelect = (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...value, [name]: e.target.value });
  };

  return (
    <div className="driver-form">
      <div className="form-row">
        <div className="form-field">
          <BaseInput
            label="Nombre"
            name="first_name"
            value={value.first_name ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            required
            autoFocus
            error={fieldErrors.first_name}
          />
        </div>

        <div className="form-field">
          <BaseInput
            label="Apellido"
            name="last_name"
            value={value.last_name ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            required
            error={fieldErrors.last_name}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <BaseInput
            label="Celular"
            name="phone"
            type="tel"
            inputMode="tel"
            value={value.phone ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            error={fieldErrors.phone}
          />
        </div>

        <div className="form-field">
          <BaseInput
            label="Dirección"
            name="address"
            value={value.address ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            error={fieldErrors.address}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field full-width">
          <MultiSelectSearchable
            label="Pases"
            name="passes"
            value={value.passes ?? []}
            options={passes}
            onChange={handlePassesChange}
            placeholder="Buscar y seleccionar pases..."
            error={fieldErrors.passes}
            menuPortal
            sortAlphabetically
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <BaseSelect
            label="Vehículo"
            name="vehicle"
            value={String(value.vehicle ?? '')}
            onChange={handleSelect('vehicle')}
            options={vehicles}
            placeholder="Seleccionar vehículo"
          />
          {fieldErrors.vehicle && (
            <div className="form-error" role="alert">
              {fieldErrors.vehicle}
            </div>
          )}
        </div>

        <div className={`form-field${!showStatusField ? ' full-width' : ''}`}>
          <BaseSelect
            label="Turno"
            name="shift"
            value={String(value.shift ?? '')}
            onChange={handleSelect('shift')}
            options={shifts}
            placeholder="Seleccionar turno"
          />
          {fieldErrors.shift && (
            <div className="form-error" role="alert">
              {fieldErrors.shift}
            </div>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-field full-width">
          <MultiSelectSearchable
            label="Grupos"
            name="shift_groups"
            value={value.shift_groups ?? []}
            options={shiftGroups}
            onChange={handleShiftGroupsChange}
            placeholder="Buscar y seleccionar grupos..."
            error={fieldErrors.shift_groups}
            menuPortal
            sortAlphabetically
          />
        </div>
      </div>

      {showStatusField && (
        <div className="form-row">
          <div className="form-field">
            <BaseSelect
              label="Estado"
              name="status"
              value={value.status ?? ''}
              onChange={handleSelect('status')}
              options={[
                { value: 'Operativo', label: 'Operativo' },
                { value: 'Descanso', label: 'Descanso' },
                { value: 'Con licencia', label: 'Con licencia' },
                { value: 'En servicio', label: 'En servicio' },
              ]}
              preserveCase
            />
            {fieldErrors.status && (
              <div className="form-error" role="alert">
                {fieldErrors.status}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default DriverForm;
